import React, { useEffect, useState, useRef, useCallback } from 'react';
import './Hero.css';
import logoT from '../assets/logo_t.png';
import ThreeJSSphere from './ThreeJSSphere';
import { useDeviceDetection } from '../hooks/useDeviceDetection';


const Hero: React.FC = () => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [rotationSpeed, setRotationSpeed] = useState(0.003);
  const [lastClickTime, setLastClickTime] = useState(0);
  const fullText = 'TERA';
  const animationRef = useRef<HTMLDivElement>(null);
  const deviceInfo = useDeviceDetection();
  const lastParticleTime = useRef(0);

  // Initialize component
  useEffect(() => {
    const readyTimer = setTimeout(() => {
      setIsReady(true);
    }, 100);

    return () => clearTimeout(readyTimer);
  }, []);

  // Typing animation with proper initialization
  useEffect(() => {
    if (!isReady) return;
    
    let index = 0;
    let typingInterval: NodeJS.Timeout;
    
    // Clear any existing text first
    setDisplayText('');
    
    // Initial delay to ensure everything is loaded
    const initialDelay = setTimeout(() => {
      typingInterval = setInterval(() => {
        if (index < fullText.length) {
          setDisplayText(fullText.slice(0, index + 1));
          index++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 350); // Slightly slower for better effect
    }, 800); // Longer delay for better first impression

    return () => {
      clearTimeout(initialDelay);
      if (typingInterval) {
        clearInterval(typingInterval);
      }
    };
  }, [isReady, fullText]);


  // Initialize Space lines
  useEffect(() => {
    const createSpaceLine = () => {
      if (!animationRef.current) return;

      const lineTypes = ['space-horizontal', 'space-vertical', 'space-diagonal', 'space-perspective'];
      const lineType = lineTypes[Math.floor(Math.random() * lineTypes.length)];
      
      const line = document.createElement('div');
      line.className = `tron-line ${lineType}`;
      
      // Full screen positioning for space effect
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      switch (lineType) {
        case 'space-horizontal':
          line.style.left = -screenWidth * 0.1 + 'px'; // Start off-screen
          line.style.top = Math.random() * screenHeight + 'px';
          break;
        case 'space-vertical':
          line.style.left = Math.random() * screenWidth + 'px';
          line.style.top = -screenHeight * 0.1 + 'px'; // Start off-screen
          break;
        case 'space-diagonal':
          const rotation = Math.random() * 180 - 90; // -90 to 90 degrees
          line.style.left = Math.random() * screenWidth * 0.5 + 'px';
          line.style.top = Math.random() * screenHeight * 0.5 + 'px';
          line.style.setProperty('--rotation', `${rotation}deg`);
          break;
        case 'space-perspective':
          const rotateX = Math.random() * 60 - 30; // -30 to 30 degrees
          const rotateY = Math.random() * 60 - 30; // -30 to 30 degrees
          line.style.left = Math.random() * screenWidth * 0.3 + screenWidth * 0.35 + 'px';
          line.style.top = Math.random() * screenHeight * 0.3 + screenHeight * 0.35 + 'px';
          line.style.setProperty('--rotateX', `${rotateX}deg`);
          line.style.setProperty('--rotateY', `${rotateY}deg`);
          break;
      }

      // Random delay and duration for variety
      line.style.animationDelay = Math.random() * 4 + 's';
      line.style.animationDuration = (8 + Math.random() * 4) + 's';
      
      animationRef.current.appendChild(line);
      
      // Remove after animation
      setTimeout(() => {
        if (line.parentNode) {
          line.parentNode.removeChild(line);
        }
      }, 15000);
    };

    const createCircuitNode = () => {
      if (!animationRef.current) return;

      const node = document.createElement('div');
      node.className = 'circuit-node';
      
      node.style.left = Math.random() * (window.innerWidth - 50) + 'px';
      node.style.top = Math.random() * (window.innerHeight - 50) + 'px';
      node.style.animationDelay = Math.random() * 2 + 's';
      
      animationRef.current.appendChild(node);
      
      // Create pulses from this node
      setTimeout(() => {
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            const pulse = document.createElement('div');
            pulse.className = 'circuit-pulse';
            pulse.style.left = node.style.left;
            pulse.style.top = node.style.top;
            
            const angle = Math.random() * Math.PI * 2;
            const distance = 200;
            pulse.style.setProperty('--end-x', Math.cos(angle) * distance + 'px');
            pulse.style.setProperty('--end-y', Math.sin(angle) * distance + 'px');
            
            animationRef.current?.appendChild(pulse);
            
            setTimeout(() => {
              if (pulse.parentNode) {
                pulse.parentNode.removeChild(pulse);
              }
            }, 3000);
          }, i * 500);
        }
      }, 1000);
      
      setTimeout(() => {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      }, 6000);
    };

    const createSpaceStar = () => {
      if (!animationRef.current) return;

      const star = document.createElement('div');
      star.className = 'space-star';
      
      star.style.left = Math.random() * window.innerWidth + 'px';
      star.style.top = Math.random() * window.innerHeight + 'px';
      star.style.animationDelay = Math.random() * 4 + 's';
      star.style.animationDuration = (3 + Math.random() * 3) + 's';
      
      animationRef.current.appendChild(star);
      
      setTimeout(() => {
        if (star.parentNode) {
          star.parentNode.removeChild(star);
        }
      }, 8000);
    };

    // Create Space elements periodically - FURTHER OPTIMIZED
    const spaceInterval = setInterval(() => {
      createSpaceLine();
      if (Math.random() > 0.95) {
        createCircuitNode();
      }
      if (Math.random() > 0.9) {
        createSpaceStar();
      }
    }, 6000);

    // Initial burst of space elements - REDUCED
    for (let i = 0; i < 2; i++) {
      setTimeout(() => createSpaceLine(), i * 2000);
    }

    // Create initial stars - REDUCED
    for (let i = 0; i < 8; i++) {
      setTimeout(() => createSpaceStar(), Math.random() * 5000);
    }

    return () => clearInterval(spaceInterval);
  }, []);

  const createMouseParticle = useCallback((x: number, y: number) => {
    if (!animationRef.current || !deviceInfo.isDesktop) return;

    const particle = document.createElement('div');
    particle.className = 'mouse-particle';
    
    // Posición de la partícula
    particle.style.left = x + 'px';
    particle.style.top = y + 'px';
    
    // Variaciones aleatorias para cada partícula
    const size = 2 + Math.random() * 4; // 2-6px
    const duration = 800 + Math.random() * 700; // 800-1500ms
    const offsetX = (Math.random() - 0.5) * 40; // -20 a 20px
    const offsetY = (Math.random() - 0.5) * 40; // -20 a 20px
    
    particle.style.width = size + 'px';
    particle.style.height = size + 'px';
    particle.style.setProperty('--offset-x', offsetX + 'px');
    particle.style.setProperty('--offset-y', offsetY + 'px');
    particle.style.setProperty('--duration', duration + 'ms');
    
    animationRef.current.appendChild(particle);
    
    // Remover partícula después del tiempo especificado
    setTimeout(() => {
      if (particle.parentNode) {
        particle.parentNode.removeChild(particle);
      }
    }, duration);
  }, [deviceInfo.isDesktop]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Solo en desktop se actualiza la posición del mouse para efectos de partículas
    if (deviceInfo.isDesktop) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setMousePos({ x, y });
      
      // Throttle particle creation - crear partícula cada 20ms para máxima densidad
      const currentTime = Date.now();
      if (currentTime - lastParticleTime.current > 20) {
        // Crear múltiples partículas por movimiento (5-7 partículas)
        createMouseParticle(x, y);
        createMouseParticle(x + (Math.random() - 0.5) * 8, y + (Math.random() - 0.5) * 8);
        createMouseParticle(x + (Math.random() - 0.5) * 12, y + (Math.random() - 0.5) * 12);
        createMouseParticle(x + (Math.random() - 0.5) * 16, y + (Math.random() - 0.5) * 16);
        if (Math.random() > 0.3) {
          createMouseParticle(x + (Math.random() - 0.5) * 20, y + (Math.random() - 0.5) * 20);
        }
        if (Math.random() > 0.4) {
          createMouseParticle(x + (Math.random() - 0.5) * 25, y + (Math.random() - 0.5) * 25);
        }
        if (Math.random() > 0.6) {
          createMouseParticle(x + (Math.random() - 0.5) * 30, y + (Math.random() - 0.5) * 30);
        }
        lastParticleTime.current = currentTime;
      }
    }
  }, [deviceInfo.isDesktop, createMouseParticle]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const currentTime = Date.now();

    // Prevent rapid clicking (debounce)
    if (currentTime - lastClickTime < 500) return;
    setLastClickTime(currentTime);

    // Create ripple effect
    const ripple = document.createElement('div');
    ripple.className = 'click-ripple';
    ripple.style.left = `${x - 25}px`;
    ripple.style.top = `${y - 25}px`;
    ripple.style.width = '50px';
    ripple.style.height = '50px';
    
    if (animationRef.current) {
      animationRef.current.appendChild(ripple);
      setTimeout(() => {
        if (ripple.parentNode) {
          ripple.parentNode.removeChild(ripple);
        }
      }, 600);
    }

    // Different click behavior for desktop vs mobile
    if (deviceInfo.isDesktop) {
      // Desktop: moderate boost that returns to normal
      setRotationSpeed(prev => prev + 0.015);
      setTimeout(() => {
        setRotationSpeed(0.003);
      }, 1500);
    } else {
      // Mobile/Tablet: stronger boost since no hover effect
      setRotationSpeed(prev => prev + 0.025);
      setTimeout(() => {
        setRotationSpeed(0.003);
      }, 2500);
    }
  }, [lastClickTime, deviceInfo.isDesktop]);

  // Handle rotation speed from Three.js sphere
  const handleRotationSpeedChange = useCallback((speed: number) => {
    setRotationSpeed(speed);
  }, []);

  // Ultra-slow scroll function specifically for Safari
  const smoothScrollTo = useCallback((targetId: string, duration: number = 5000) => {
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    // Aggressively disable all native scroll behaviors
    const originalHtmlBehavior = document.documentElement.style.scrollBehavior;
    const originalBodyBehavior = document.body.style.scrollBehavior;
    
    document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
    document.body.style.setProperty('scroll-behavior', 'auto', 'important');
    
    // Disable smooth scrolling on the root element
    document.documentElement.style.setProperty('-webkit-scroll-behavior', 'auto', 'important');
    document.body.style.setProperty('-webkit-scroll-behavior', 'auto', 'important');

    const startPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
    const targetPosition = targetElement.offsetTop - 100; // Slight offset for better UX
    const distance = targetPosition - startPosition;
    
    let startTime: number | null = null;
    let animationId: number | null = null;

    // Very slow easing function
    const easeInOutSine = (t: number): number => {
      return -(Math.cos(Math.PI * t) - 1) / 2;
    };

    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutSine(progress);
      
      const currentPos = startPosition + (distance * eased);
      
      // Force scroll position using multiple methods
      if ('scrollTo' in window) {
        window.scrollTo(0, currentPos);
      }
      if (document.documentElement.scrollTop !== undefined) {
        document.documentElement.scrollTop = currentPos;
      }
      if (document.body.scrollTop !== undefined) {
        document.body.scrollTop = currentPos;
      }
      
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        // Ensure we end at the exact target
        window.scrollTo(0, targetPosition);
        document.documentElement.scrollTop = targetPosition;
        
        // Restore original scroll behavior after animation
        setTimeout(() => {
          document.documentElement.style.scrollBehavior = originalHtmlBehavior;
          document.body.style.scrollBehavior = originalBodyBehavior;
        }, 100);
      }
    };

    animationId = requestAnimationFrame(animate);

    // Return cleanup function
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      document.documentElement.style.scrollBehavior = originalHtmlBehavior;
      document.body.style.scrollBehavior = originalBodyBehavior;
    };
  }, []);

  return (
    <section 
      id="inicio" 
      className="hero"
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <div className="hero-background">
        <div className="tron-lines"></div>
        <ThreeJSSphere 
          mousePos={mousePos}
          onRotationSpeedChange={handleRotationSpeedChange}
          isDesktop={deviceInfo.isDesktop}
        />
        <div className="geometric-animation" ref={animationRef}>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
        </div>
        <div className="hero-overlay"></div>
        {deviceInfo.isDesktop ? (
          <>
            <img src={logoT} alt="TERA Logo" className="hero-logo-left" />
            <img src={logoT} alt="TERA Logo" className="hero-logo-right" />
          </>
        ) : (
          <img src={logoT} alt="TERA Logo" className="hero-logo-center" />
        )}
        <div 
          className="cursor-glow" 
          style={{ 
            left: mousePos.x - 100, 
            top: mousePos.y - 100,
            opacity: mousePos.x > 0 ? 0.5 : 0
          }}
        />
      </div>
      <div className="hero-content">
        <h1 className={`hero-title ${!isTyping ? 'glitch' : ''}`}>
          {displayText}
          {isTyping && <span className="cursor">|</span>}
        </h1>
        <p className="hero-subtitle">
          Tecnología que impulsa tu empresa hacia el futuro
        </p>
        <p className="hero-description">
          Porque el futuro no se espera. Se construye.
        </p>
        <div className="hero-buttons">
          <button className="btn-primary" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            smoothScrollTo('soluciones', 1500);
          }}>
            Nuestras Soluciones
          </button>
          <button className="btn-secondary" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            smoothScrollTo('contacto', 1500);
          }}>
            Contáctanos
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;